import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { timeAxisZoom, timeChartToolbox, ZOOMED_GRID_BOTTOM } from './timeZoom';

// Gamma counts against time for a GammaTrak run.
//
// Unlike the gyro and orientation charts this is a single channel: the tool logs
// one count reading per interval for the whole run. The trace is deliberately
// left unsmoothed — a hot zone shows up as a handful of samples an order of
// magnitude above the baseline, and any rolling average wide enough to settle
// the baseline noise also flattens the peak that identifies it.
const COUNTS_COLOUR = '#5470c6';

export const GammaRecordChart = ({ gammaRecords, depthRecords, height = 400 }) => {
    const { t } = useTranslation();
    // Memoised so a parent re-render does not re-apply the option and throw
    // away the user's zoom (see timeZoom.js).
    const option = useMemo(() => {
        // Chronological order: the stored order is whatever the app wrote, and a time
        // axis fed out-of-order points draws a line that doubles back on itself.
        const records = [...(gammaRecords || [])]
            .filter(record => record && Number.isFinite(Number(record.Counts)))
            .sort((a, b) => new Date(a.TimeStamp) - new Date(b.TimeStamp));

        // Depth marks, where the operator set any, as vertical lines — same treatment
        // as the gyro chart so the two read alike.
        const depthMarkLines = (depthRecords || []).map(record => ({
            xAxis: record.TimeStamp,
            name: record && record.Depth !== undefined ? `${record.Depth}` : ''
        }));

        return {
            title: {
                text: t('charts.gammaRecordData'),
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            toolbox: timeChartToolbox,
            // Zoom matters more here than on the other record charts: a single hot
            // zone sets the axis maximum, and narrowing the window to the baseline
            // either side of it lets the axis rescale to that baseline.
            dataZoom: timeAxisZoom(),
            grid: {
                bottom: ZOOMED_GRID_BOTTOM
            },
            xAxis: {
                type: 'time',
                scale: true,
                splitLine: {
                    show: true
                }
            },
            yAxis: {
                name: t('charts.counts'),
                type: 'value',
                // Counts are a rate with a meaningful zero, so the axis is anchored
                // there rather than auto-scaled to the data floor: a floor at the
                // lowest sample would inflate ordinary counting noise into what looks
                // like a real change in the formation.
                min: 0,
                axisLabel: {
                    formatter: '{value}'
                },
                splitLine: {
                    show: true
                }
            },
            series: [
                {
                    name: t('charts.counts'),
                    type: 'line',
                    smooth: false,
                    showSymbol: false,
                    itemStyle: { color: COUNTS_COLOUR },
                    lineStyle: { width: 1.5 },
                    data: records.map(record => [record.TimeStamp, Number(record.Counts)])
                },
                // Invisible helper series carrying the depth markLines, matching
                // GyroRecordChart.
                {
                    name: 'DepthMarks',
                    type: 'line',
                    data: [],
                    tooltip: { show: false },
                    showSymbol: false,
                    silent: true,
                    markLine: {
                        symbol: ['none', 'none'],
                        silent: true,
                        zlevel: 10,
                        label: {
                            formatter: params => params.name || '',
                            position: 'end',
                            color: '#333',
                            fontSize: 11
                        },
                        lineStyle: {
                            color: 'rgba(255, 0, 0, 0.7)',
                            type: 'dashed',
                            width: 1
                        },
                        data: depthMarkLines
                    }
                }
            ]
        };
    }, [gammaRecords, depthRecords, t]);

    return (
        <ReactEcharts
            option={option}
            // Same reason as GyroRecordChart: replace rather than merge, so a
            // previous survey's series can't be stranded on the canvas.
            notMerge={true}
            style={{ height: height + 'px', width: '100%' }}
        />
    );
};
